import topApi from '../../components/api/ToPApi';

// Pantalla de cocina (KDS): comandas por estado. Las reglas las valida el API (services/kitchen-display.ts).

export type TicketStatus = 'PEN' | 'PRE' | 'LIS' | 'ENT';
export type TicketType = 'MAR' | 'ANU';
export type AlertLevel = 'ok' | 'warn' | 'late';

export interface KitchenItem {
    name: string;
    quantity: number;
    note: string | null;
    course: number;
}

export interface KitchenTicket {
    id: number;
    orderId: number;
    orderNumber: number | null;
    table: string | null;
    takeout: boolean;
    waiter: string | null;
    orderNote: string | null;
    type: TicketType;
    status: TicketStatus;
    courses: string | null;
    firedAt: string;
    startedAt: string | null;
    readyAt: string | null;
    deliveredAt: string | null;
    items: KitchenItem[];
}

export const COURSE_NAMES: Record<number, string> = { 0: 'Sin tiempo', 1: 'Entradas', 2: 'Platos fuertes', 3: 'Postres' };

export const NEXT_STATUS: Record<TicketStatus, TicketStatus | null> = { PEN: 'PRE', PRE: 'LIS', LIS: 'ENT', ENT: null };
export const PREVIOUS_STATUS: Record<TicketStatus, TicketStatus | null> = { PEN: null, PRE: 'PEN', LIS: 'PRE', ENT: 'LIS' };

export const NEXT_LABEL: Record<TicketStatus, string> = { PEN: 'Iniciar', PRE: 'Lista', LIS: 'Entregada', ENT: '' };

export interface AlertSettings {
    warn: number;
    late: number;
    sound: boolean;
}

export const DEFAULT_SETTINGS: AlertSettings = { warn: 10, late: 20, sound: true };

const SETTINGS_KEY = 'kitchen-display-settings';

// Ajustes de esta pantalla (cada cocina puede tener los suyos)
export const loadSettings = (): AlertSettings => {
    try {
        const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null');
        return saved ? { ...DEFAULT_SETTINGS, ...saved } : DEFAULT_SETTINGS;
    } catch {
        return DEFAULT_SETTINGS;
    }
};

export const saveSettings = (settings: AlertSettings) => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* sin almacenamiento */ }
};

export const minutesSince = (from: string, now: number) => Math.max(0, Math.floor((now - new Date(from).getTime()) / 60000));

export const alertLevel = (ticket: KitchenTicket, now: number, s: AlertSettings): AlertLevel => {
    if (ticket.type === 'ANU' || ticket.status === 'LIS' || ticket.status === 'ENT') return 'ok';
    const minutes = minutesSince(ticket.firedAt, now);
    if (minutes >= s.late) return 'late';
    if (minutes >= s.warn) return 'warn';
    return 'ok';
};

// Tiempo transcurrido como lo lee la cocina: "4 min", "1 h 05"
export const elapsedText = (minutes: number) =>
    minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;

// Productos agrupados por tiempo, en orden (entradas antes que fuertes)
export const itemsByCourse = (items: KitchenItem[]) => {
    const groups = new Map<number, KitchenItem[]>();
    [...items].sort((a, b) => a.course - b.course).forEach(i => groups.set(i.course, [...(groups.get(i.course) ?? []), i]));
    return [...groups.entries()].map(([course, list]) => ({ course, name: COURSE_NAMES[course] ?? COURSE_NAMES[0], items: list }));
};

// Ids de comandas nuevas (para sonar solo cuando llega algo)
export const newTicketIds = (before: KitchenTicket[], after: KitchenTicket[]) => {
    const known = new Set(before.map(t => t.id));
    return after.filter(t => !known.has(t.id) && t.status === 'PEN').map(t => t.id);
};

const errorMessage = (e: any) => e?.response?.data?.error || 'Ocurrió un error de comunicación, intente de nuevo.';

export interface KitchenBoard {
    tickets: KitchenTicket[];
    serverTime: string;
    /** El comercio trabaja con la pantalla (al marchar no se imprime). Por defecto sí. */
    kitchenDisplay?: boolean;
}

export const loadBoard = async (): Promise<KitchenBoard> => {
    try {
        const resp = await topApi.get<KitchenBoard>('/kitchen/board');
        return resp.data;
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

/** Enciende o apaga la pantalla de cocina del comercio (apagada, los equipos imprimen la comanda). */
export const saveKitchenDisplay = async (kitchenDisplay: boolean): Promise<void> => {
    try {
        await topApi.put('/kitchen/settings', { kitchenDisplay });
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};

export const setTicketStatus = async (id: number, status: TicketStatus): Promise<void> => {
    try {
        await topApi.put(`/kitchen/tickets/${id}/status`, { status });
    } catch (e) {
        throw new Error(errorMessage(e));
    }
};
